import type { KeyboardEvent as ReactKeyboardEvent, ReactElement, ReactNode } from 'react'
import { cloneElement, isValidElement, useEffect, useId, useRef, useState } from 'react'
import { cn } from '@/lib/cn'

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right'

export interface TooltipProps {
  content: ReactNode
  side?: TooltipSide
  children: ReactNode
}

const sides: Record<TooltipSide, string> = {
  top: 'bottom-full left-1/2 mb-2 -translate-x-1/2',
  bottom: 'top-full left-1/2 mt-2 -translate-x-1/2',
  left: 'right-full top-1/2 mr-2 -translate-y-1/2',
  right: 'left-full top-1/2 ml-2 -translate-y-1/2'
}

const OPEN_DELAY_MS = 250

export function Tooltip({ content, side = 'top', children }: TooltipProps): ReactElement {
  const [open, setOpen] = useState(false)
  const timer = useRef<number | null>(null)
  const generatedId = useId()
  const tooltipId = `tooltip-${generatedId}`

  const cancel = (): void => {
    if (timer.current === null) return
    window.clearTimeout(timer.current)
    timer.current = null
  }

  const show = (immediate: boolean): void => {
    cancel()
    if (immediate) {
      setOpen(true)
      return
    }
    timer.current = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS)
  }

  const hide = (): void => {
    cancel()
    setOpen(false)
  }

  useEffect(() => cancel, [])

  const onKeyDown = (event: ReactKeyboardEvent<HTMLSpanElement>): void => {
    if (event.key !== 'Escape' || !open) return
    // Only swallow Escape while visible, so a dialog behind us still closes.
    event.stopPropagation()
    hide()
  }

  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        'aria-describedby': open ? tooltipId : undefined
      })
    : children

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => show(false)}
      onMouseLeave={hide}
      onFocusCapture={() => show(true)}
      onBlurCapture={hide}
      onKeyDown={onKeyDown}
    >
      {trigger}
      {open ? (
        <span
          role="tooltip"
          id={tooltipId}
          className={cn(
            'animate-rise pointer-events-none absolute z-50 max-w-[16rem] rounded-control bg-ink-900 px-2.5 py-1.5 text-xs font-medium leading-snug text-ink-50 shadow-pop dark:bg-ink-100 dark:text-ink-900',
            sides[side]
          )}
        >
          {content}
        </span>
      ) : null}
    </span>
  )
}
